import type { ReactNode } from 'react';
import { Icon } from '@/components/ui/Icon';

/**
 * סרגלי הכלים של "לידים ואנשי קשר" לפי עיצוב v2: שורה עליונה = בורר
 * המקטעים (לידים / אנשי קשר) ומסנני בחירה מימין ופעולות משמאל; שורה שנייה = חיפוש
 * רחב + בורר סטטוסים.
 */
export function ScreenToolbar({ switcher, filters, actions }: { switcher?: ReactNode; filters?: ReactNode; actions: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {switcher}
      {filters && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>{filters}</div>}
      <span style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>
    </div>
  );
}

export function FilterRow({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>{children}</div>;
}

/** שדה חיפוש בסגנון כרטיס (לבן, צל רך, בלי מסגרת) — ממלא את שארית השורה. */
export function SearchField({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label style={{
      flex: '1 1 260px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, height: 40,
      padding: '0 14px', borderRadius: 10, background: 'var(--surface)', boxShadow: 'var(--shadow-1)',
      color: 'var(--muted)', cursor: 'text',
    }}>
      <Icon name="search" size={16} />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{
          flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent',
          color: 'var(--text)', fontSize: 'var(--t-sm)', fontFamily: 'inherit',
        }}
      />
    </label>
  );
}

// צבע נקודת הקמפיין — יציב לפי מזהה, מתוך טוקני המערכת בלבד.
const CAMPAIGN_DOTS = ['var(--accent)', 'var(--warn)', 'var(--busy)', 'var(--ok)', 'var(--muted)'];

export function CampaignCell({ id, name }: { id?: number | null; name?: string | null }) {
  if (!name) return <>—</>;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--text-2)', minWidth: 0 }}>
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: 99, flex: 'none', background: CAMPAIGN_DOTS[(id ?? 0) % CAMPAIGN_DOTS.length] }} />
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
    </span>
  );
}

/** "לפני 12 דק׳" / "לפני שעה" / "היום 09:40" / "אתמול 17:20" / "לפני 3 ימים" / תאריך. */
export function timeAgo(iso?: string | null, now: Date = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const min = Math.floor((now.getTime() - d.getTime()) / 60000);
  const hm = d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  if (min < 1) return 'עכשיו';
  if (min < 60) return `לפני ${min} דק׳`;
  if (min < 120) return 'לפני שעה';
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86400000);
  if (days === 0) return `היום ${hm}`;
  if (days === 1) return `אתמול ${hm}`;
  if (days < 7) return `לפני ${days} ימים`;
  return d.toLocaleDateString('he-IL');
}

export function MutedCell({ children }: { children: ReactNode }) {
  return <span style={{ color: 'var(--muted)', fontSize: 12.5, whiteSpace: 'nowrap' }}>{children}</span>;
}
